<script setup lang="ts">
import { ref, watch } from 'vue'
import { useMagicKeys } from '@vueuse/core'
import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'

const emits = defineEmits<{
    (e: 'opened'): void,
}>()

const isOpen = ref(false)

// Setup keyboard shortcut
const { meta_k, ctrl_k } = useMagicKeys()

// Reset query when opening the palette
watch(isOpen, (newValue, oldValue) => {
  if (newValue && !oldValue) {
    emits('opened')
  }
})

// Watch for keyboard shortcuts
watch([meta_k, ctrl_k], ([metaK, ctrlK]) => {
  if (metaK || ctrlK) {
    isOpen.value = true
  }
})

defineExpose({
  close: () => isOpen.value = false,
})
</script>

<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" class="relative z-50" @close="isOpen = false">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/50" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto pt-[20vh]">
        <div class="flex min-h-full items-start justify-center p-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              class="w-full max-w-xl transform overflow-hidden rounded-lg bg-white text-left align-middle shadow-xl transition-all"
            >
              <slot @close="isOpen = false" />
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<style>
.vue-command-palette {
  @apply fixed inset-0 z-50;
}

.vue-command-palette-overlay {
  @apply fixed inset-0 bg-black/50;
}

.vue-command-palette-content {
  @apply fixed top-[20%] left-1/2 -translate-x-1/2 w-full max-w-xl bg-white rounded-lg shadow-2xl;
}

.vue-command-palette-search {
  @apply w-full px-4 py-3 text-lg border-b focus:outline-none;
}

.vue-command-palette-commands {
  @apply max-h-[60vh] overflow-y-auto;
}

.vue-command-palette-command {
  @apply px-4 py-3 hover:bg-gray-100 cursor-pointer;
}

.vue-command-palette-command[aria-selected='true'] {
  @apply bg-gray-100;
}
</style>
